import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { questions } from '../data/questions';
import './Quiz.css';

const Quiz = ({
  currentQuestion,
  answers,
  onAnswer,
  onPrevious
}) => {
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="quiz-container">
      <div className="quiz-wrapper">
        {/* Progress Bar */}
        <div className="quiz-progress">
          <div className="quiz-progress-header">
            <span className="quiz-progress-text">Your Journey</span>
            <span className="quiz-progress-percentage">{Math.round(progress)}% Complete</span>
          </div>
          <div className="quiz-progress-bar">
            <motion.div
              className="quiz-progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="quiz-question-card"
          >
            {/* Question Number Badge */}
            <div className="flex justify-between items-start mb-6">
              <span className="quiz-question-number">
                Question {currentQuestion + 1}
              </span>

              {/* Final Question Badge */}
              {isLastQuestion && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-sm font-semibold px-3 py-1.5 rounded-full border border-green-100"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Final Step!</span>
                </motion.div>
              )}
            </div>

            {/* Question Text */}
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="quiz-question-title"
            >
              {question.question}
            </motion.h2>

            {/* Question Description */}
            {question.description && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-gray-600 mb-8 leading-relaxed"
              >
                {question.description}
              </motion.p>
            )}

            {/* Answer Options */}
            <div className="quiz-options">
              {question.options.map((option, index) => {
                const isSelected = answers[question.id] === option.value;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + (index * 0.05) }}
                  >
                    <button
                      onClick={() => onAnswer(question.id, option.value)}
                      className={`quiz-option ${isSelected ? 'quiz-option-selected' : ''}`}
                    >
                      <div className="quiz-option-indicator">
                        {isSelected && (
                          <motion.svg
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-3.5 h-3.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </motion.svg>
                        )}
                      </div>
                      <span className="quiz-option-text">{option.label}</span>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="quiz-navigation">
          {currentQuestion > 0 ? (
            <button
              onClick={onPrevious}
              className="quiz-nav-back"
            >
              <ArrowLeft />
              <span>Previous</span>
            </button>
          ) : (
            <div />
          )}

          <div className="quiz-hint">
            {isLastQuestion ? (
              <span className="quiz-hint-final flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                </svg>
                Select to see results
              </span>
            ) : (
              <span>Select an option to continue</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
